import { createContext, useContext, useEffect, useState, useCallback, useRef, useMemo } from "react";
import { supabase, fmtError } from "@/lib/supabase";

const AuthContext = createContext(null);

// We `select *` (instead of an explicit list) so the auth bootstrap still
// works when the deployed schema is behind the frontend (e.g. v8 migration
// not yet run). The schema fields we read are documented below.
//   id, display_name, username, full_name, birthday, gender, avatar_url,
//   bio, city, [v8]: state, country, location_lat, location_lng,
//   whatsapp, instagram, snapchat,
//   role, is_suspended, is_verified_admin
const PROFILE_COLUMNS = "*";

async function ensureProfile(user, extras = {}) {
  if (!user) return;
  const payload = {
    id: user.id,
    display_name:
      extras.display_name ||
      user.user_metadata?.display_name ||
      user.user_metadata?.full_name ||
      user.email?.split("@")[0] ||
      "anon",
  };
  if (extras.username) payload.username = String(extras.username).toLowerCase();
  if (extras.full_name) payload.full_name = extras.full_name;
  if (extras.birthday) payload.birthday = extras.birthday;
  if (extras.gender) payload.gender = extras.gender;
  if (extras.city) payload.city = extras.city;
  // The next four columns ship with migration v8. We try them but catch
  // failures so accounts created before the migration still work.
  if (extras.state) payload.state = extras.state;
  if (extras.country) payload.country = extras.country;
  if (extras.location_lat != null) payload.location_lat = extras.location_lat;
  if (extras.location_lng != null) payload.location_lng = extras.location_lng;
  const { error } = await supabase.from("profiles").upsert(payload, { onConflict: "id" });
  if (error && /(state|country|location_lat|location_lng)/i.test(error.message || "")) {
    // Fallback: re-upsert without the v8 columns when DB is on v7.
    const { state: _s, country: _c, location_lat: _la, location_lng: _ln, ...legacy } = payload;
    void _s; void _c; void _la; void _ln;
    await supabase.from("profiles").upsert(legacy, { onConflict: "id" });
  }
}

export function AuthProvider({ children }) {
  // status: 'loading' | 'signed_in' | 'signed_out'
  const [status, setStatus] = useState("loading");
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const mountedRef = useRef(true);

  const loadProfile = useCallback(async (u) => {
    if (!u) {
      setProfile(null);
      return;
    }
    const { data } = await supabase
      .from("profiles")
      .select(PROFILE_COLUMNS)
      .eq("id", u.id)
      .maybeSingle();
    if (!mountedRef.current) return;
    if (data) setProfile(data);
    else {
      await ensureProfile(u);
      const { data: data2 } = await supabase
        .from("profiles")
        .select(PROFILE_COLUMNS)
        .eq("id", u.id)
        .maybeSingle();
      if (mountedRef.current) setProfile(data2 || { id: u.id, display_name: "anon" });
    }
  }, []);

  // Apply session once. Treat the *first* getSession() as the source of truth.
  useEffect(() => {
    mountedRef.current = true;

    const apply = async (session) => {
      if (!mountedRef.current) return;
      const u = session?.user || null;
      setUser(u);
      // Resolve profile BEFORE flipping status away from 'loading' so that
      // role-gated routes (e.g. AdminRoute) don't see a transient
      // profile===null state and redirect away on direct nav / refresh.
      await loadProfile(u);
      if (!mountedRef.current) return;
      setStatus(u ? "signed_in" : "signed_out");
    };

    // Subscribe FIRST so we don't miss INITIAL_SESSION on some browsers.
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      apply(session);
    });

    // Trigger initial resolution.
    supabase.auth.getSession().then(({ data }) => apply(data.session));

    return () => {
      mountedRef.current = false;
      sub.subscription.unsubscribe();
    };
  }, [loadProfile]);

  // Welcome email — fires exactly once per user on the first authenticated
  // session whose profile has `welcome_email_sent = false`. Idempotency is
  // owned by Postgres (the boolean flag); React state just gates re-entry
  // within a tab. Failures are logged-only so auth is never blocked.
  //
  // Why this design:
  //   • Bound to (user, profile) instead of `onAuthStateChange` events:
  //     Supabase emits multiple SIGNED_IN events for token refresh / tab
  //     refocus, so an event-bound trigger would over-fire.
  //   • Re-reads `welcome_email_sent` fresh from the DB right before
  //     calling the function. We deliberately do NOT rely on the cached
  //     `profile` object here because (a) the loadProfile fallback can
  //     return a minimal stub `{id, display_name}` that lacks the column
  //     entirely, and (b) PostgREST schema-cache lag right after the
  //     migration can hide the field for one or two reads. A fresh read
  //     is one extra request but eliminates the whole class of bug.
  //   • A `useRef` keeps a tab-local guard so an in-flight call can't be
  //     racing with a parallel re-render.
  //   • Uses `supabase.functions.invoke('welcome-email', ...)` so the
  //     Authorization + apikey headers are attached automatically.
  //
  // Logging: production stays quiet (only `console.warn` on failure). Add
  // `?debug=welcome` to the URL to re-enable the verbose per-branch trace
  // used during the initial wire-up.
  const welcomeAttemptedForUserRef = useRef(null);
  useEffect(() => {
    const debug =
      typeof window !== "undefined" &&
      new URLSearchParams(window.location.search).get("debug") === "welcome";
    // eslint-disable-next-line no-console
    const trace = debug ? (...args) => console.log("[welcome-email]", ...args) : () => {};

    if (!user?.id) { trace("skip: no user"); return; }
    if (!profile) { trace("skip: profile not loaded yet", user.id); return; }
    if (welcomeAttemptedForUserRef.current === user.id) {
      trace("skip: already attempted in this tab", user.id);
      return;
    }
    welcomeAttemptedForUserRef.current = user.id;

    const userId = user.id;
    trace("effect entry", {
      userId,
      email: user.email,
      profileWelcomeFlagCached: profile.welcome_email_sent,
    });

    (async () => {
      const { data: row, error: readErr } = await supabase
        .from("profiles")
        .select("welcome_email_sent")
        .eq("id", userId)
        .maybeSingle();
      trace("fresh flag read", { row, readErr });

      if (readErr) {
        // eslint-disable-next-line no-console
        console.warn(
          "[welcome-email] could not read welcome_email_sent — is the migration applied?",
          readErr
        );
        return;
      }
      if (!row || row.welcome_email_sent === true) {
        trace("skip: already sent (or no row)", row);
        return;
      }

      const email = user.email || profile.email || null;
      const name =
        profile.display_name ||
        profile.full_name ||
        user.user_metadata?.full_name ||
        user.user_metadata?.display_name ||
        "";
      if (!email) {
        // eslint-disable-next-line no-console
        console.warn("[welcome-email] skip: no email available on user/profile", { userId });
        welcomeAttemptedForUserRef.current = null;
        return;
      }

      try {
        trace("invoking edge function…", { email, name });
        const { data, error } = await supabase.functions.invoke("welcome-email", {
          body: { email, name },
        });
        if (error) {
          // eslint-disable-next-line no-console
          console.warn("[welcome-email] function returned error", error);
          welcomeAttemptedForUserRef.current = null; // allow retry next session
          return;
        }
        trace("invoke success", data);

        const { error: upErr } = await supabase
          .from("profiles")
          .update({ welcome_email_sent: true })
          .eq("id", userId);
        if (upErr) {
          // The email DID go out — we just couldn't mark it sent. Don't
          // retry on next session (would double-send); log loudly.
          // eslint-disable-next-line no-console
          console.warn("[welcome-email] sent but flag update failed", upErr);
          return;
        }
        // eslint-disable-next-line no-console
        console.log("[welcome-email] sent ✓");
        if (mountedRef.current) {
          setProfile((p) => (p ? { ...p, welcome_email_sent: true } : p));
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("[welcome-email] unexpected error", err);
        welcomeAttemptedForUserRef.current = null;
      }
    })();
  }, [user, profile]);

  /**
   * signUp(opts):
   *   { email, password, username, full_name, display_name, birthday, gender }
   * Supabase email confirmation behavior:
   *   - If "Confirm email" is ON in the project, signUp returns session = null
   *     and Supabase sends a confirmation email automatically.
   *   - If OFF, signUp returns a session and user.email_confirmed_at is set.
   *   We just forward Supabase's behavior either way.
   */
  const signUp = async (opts) => {
    const {
      email,
      password,
      username,
      full_name,
      display_name,
      birthday,
      gender,
    } = opts || {};

    const cleanUsername = String(username || "").trim().toLowerCase();
    const meta = {
      username: cleanUsername,
      full_name: full_name?.trim() || null,
      display_name: display_name?.trim() || full_name?.trim() || cleanUsername,
      birthday: birthday || null,
      gender: gender || null,
    };

    const { data, error } = await supabase.auth.signUp({
      email: email?.trim(),
      password,
      options: {
        data: meta,
        emailRedirectTo: `${window.location.origin}/feed`,
      },
    });
    if (error) return { ok: false, error: fmtError(error) };

    if (data.user) {
      await ensureProfile(data.user, {
        username: cleanUsername,
        full_name: meta.full_name,
        display_name: meta.display_name,
        birthday: meta.birthday,
        gender: meta.gender,
        city: opts.city || null,
        state: opts.state || null,
        country: opts.country || null,
        location_lat: opts.location_lat ?? null,
        location_lng: opts.location_lng ?? null,
      });
    }
    if (!data.session) {
      return {
        ok: true,
        needsConfirm: true,
        message: "Check your email to confirm your account.",
      };
    }
    return { ok: true, needsConfirm: false };
  };

  const signIn = async (identifier, password) => {
    // Accepts either an email or a username.
    let email = (identifier || "").trim();
    if (email && !email.includes("@")) {
      // Resolve username → email via SECURITY DEFINER RPC.
      const { data: resolved, error: rpcErr } = await supabase
        .rpc("email_for_username", { p_username: email });
      if (rpcErr || !resolved) {
        return { ok: false, error: "No account with that username." };
      }
      email = String(resolved).trim();
    }
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) return { ok: false, error: fmtError(error) };
    if (data.user) await ensureProfile(data.user);
    return { ok: true };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setProfile(null);
    setStatus("signed_out");
  };

  const refreshProfile = useCallback(async () => {
    if (user) await loadProfile(user);
  }, [user, loadProfile]);

  // Resend the signup confirmation email.
  const resendConfirmation = async (email) => {
    const target = (email || user?.email || "").trim();
    if (!target) return { ok: false, error: "No email on file." };
    const { error } = await supabase.auth.resend({
      type: "signup",
      email: target,
      options: { emailRedirectTo: `${window.location.origin}/feed` },
    });
    if (error) return { ok: false, error: fmtError(error) };
    return { ok: true };
  };

  const emailVerified = useMemo(
    () => !!user?.email_confirmed_at || !!user?.confirmed_at,
    [user?.email_confirmed_at, user?.confirmed_at]
  );

  const isAdmin = useMemo(() => profile?.role === "admin", [profile?.role]);

  // First-time onboarding tour. Source of truth is the boolean column
  // `profiles.has_completed_onboarding`. If the column is missing (migration
  // not yet applied), we fall back to `localStorage` so the feature still
  // works without blocking. localStorage key is per-user-id so multiple
  // accounts on the same device behave correctly.
  //
  // Auto-trigger fires once we have both user + profile AND the column
  // (or the localStorage flag) indicates not-yet-completed. Subsequent
  // logins / refreshes short-circuit on the flag.
  const [onboardingOpen, setOnboardingOpen] = useState(false);
  const onboardingAutoFiredForUserRef = useRef(null);
  const lsKey = (uid) => `inonit:onboarding-done:${uid}`;
  useEffect(() => {
    if (!user?.id || !profile) return;
    if (onboardingAutoFiredForUserRef.current === user.id) return;

    // Column missing → consult localStorage so we don't repeatedly open
    // the modal on every page render of a returning user.
    let completed = false;
    if (Object.prototype.hasOwnProperty.call(profile, "has_completed_onboarding")) {
      completed = profile.has_completed_onboarding === true;
    } else {
      try { completed = localStorage.getItem(lsKey(user.id)) === "1"; } catch { /* ignore */ }
    }
    onboardingAutoFiredForUserRef.current = user.id;
    if (!completed) setOnboardingOpen(true);
  }, [user, profile]);

  const persistOnboardingComplete = async () => {
    if (!user?.id) return;
    try { localStorage.setItem(lsKey(user.id), "1"); } catch { /* ignore */ }
    try {
      const { error } = await supabase
        .from("profiles")
        .update({ has_completed_onboarding: true })
        .eq("id", user.id);
      if (error) return; // Column likely missing — localStorage already set.
      if (mountedRef.current) {
        setProfile((p) => (p ? { ...p, has_completed_onboarding: true } : p));
      }
    } catch { /* never block auth on this */ }
  };

  const openOnboarding   = () => setOnboardingOpen(true);
  const closeOnboarding  = () => { setOnboardingOpen(false); persistOnboardingComplete(); };
  const completeOnboarding = () => { setOnboardingOpen(false); persistOnboardingComplete(); };

  return (
    <AuthContext.Provider
      value={{
        user,
        profile,
        status,
        loading: status === "loading",
        emailVerified,
        isAdmin,
        signIn,
        signUp,
        signOut,
        refreshProfile,
        resendConfirmation,
        onboardingOpen,
        openOnboarding,
        closeOnboarding,
        completeOnboarding,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
